import { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";

/**
 * Single source of truth for EVERYTHING a user owns
 * - canonical items (items table)
 * - user-specific / non-canonical items
 */
export function useUserCollectionData({
  userId,
  categoryId = null,
  subcategoryId = null,
  year = null,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;

    const fetchCollection = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase
          .from("user_items")
          .select(`
            id,
            user_id,
            item_id,
            condition,
            price_paid,
            estimated_value,
            created_at,

            user_specific_name,
            user_specific_set_name,
            user_specific_brand,
            user_specific_year,

            attributes,
            description,

            items (
              id,
              name,
              brand,
              set_name,
              year,
              category_id,
              subcategory_id
            )
          `)
          .eq("user_id", userId);

        if (error) throw error;

        const normalized = (data || [])
          .map(row => {
            const canonical = row.items;

            return {
              id: row.id,

              // 🧠 Canonical → user-specific → fallback
              name:
                canonical?.name ??
                row.user_specific_name ??
                row.description ??
                "Unknown Item",

              brand:
                canonical?.brand ??
                row.user_specific_brand ??
                null,

              set_name:
                canonical?.set_name ??
                row.user_specific_set_name ??
                null,

              year:
                canonical?.year ??
                row.user_specific_year ??
                null,

              category_id: canonical?.category_id ?? null,
              subcategory_id: canonical?.subcategory_id ?? null,

              condition: row.condition,
              price_paid: row.price_paid,
              estimated_value: row.estimated_value,

              extra_data: row.attributes,
              created_at: row.created_at,

              is_canonical: !!canonical,
            };
          })
          .filter(item => {
            if (categoryId && item.category_id !== categoryId) return false;
            if (subcategoryId && item.subcategory_id !== subcategoryId) return false;
            if (year && item.year !== year) return false;
            return true;
          });

        setData(normalized);
      } catch (err) {
        console.error("Collection fetch failed:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [userId, categoryId, subcategoryId, year]);

  return { data, loading, error };
}
